import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  CustomAttributeInput,
  ObjectType,
  User,
  UserInput,
} from '@core/generated/gql-types';
import { userActions } from '@core/user/user.action';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { getApolloClient } from '@core/lib/getApolloClient';
import { PROCESS_PARTIAL_UPDATES } from '@core/generic/partiallyUpdate.gql';
import { PASSPORT_COUNTRY_OPTIONS } from '@/utils/countryOptions';
import {
  formatDobForInput,
  formatDobForApi,
  getCustomAttribute,
} from '@/utils/utils';
import {
  actionFieldInputClassName,
  actionListboxButtonClassName,
  actionListboxOptionActiveClassName,
  actionPrimaryButtonClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';
import { RESIDENCY_STATUS_OPTIONS } from '@/components/worker/utils/workEligibility.utils';
import ActionListboxField from '@/components/shared/ActionListboxField';

export interface WorkEligibilityModalProps {
  worker: User;
  isOpen: boolean;
  onClose: () => void;
}

const WorkEligibilityModal: React.FC<WorkEligibilityModalProps> = ({
  worker,
  isOpen,
  onClose,
}) => {
  const { fetchDetailedWorkerById } = useWorkerStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');

  const initialValues = useMemo(() => {
    const rawCountry =
      getCustomAttribute(worker, 'passportIssueCountry') ||
      worker.address?.countryValue ||
      '';
    const countryOption = PASSPORT_COUNTRY_OPTIONS.find(
      (o) => o.value === rawCountry || o.label === rawCountry,
    );
    const passportIssueCountry = countryOption
      ? countryOption.label
      : rawCountry;

    const residencyStatusCandidates = [
      getCustomAttribute(worker, 'residencyStatusWithMandatoryValidVisa'),
      getCustomAttribute(worker, 'residencyStatus'),
      worker.vevoCheck?.residencyStatus,
    ];
    const residencyStatus =
      residencyStatusCandidates
        .find((candidate) =>
          RESIDENCY_STATUS_OPTIONS.some(
            (option) => option.value === candidate?.trim(),
          ),
        )
        ?.trim() || '';

    return {
      residencyStatus,
      dob: formatDobForInput(worker.dob),
      passportNumber: getCustomAttribute(worker, 'passportNumber') || '',
      passportIssueCountry,
      lastName: worker.lastName || '',
      firstName: worker.firstName || '',
    };
  }, [worker]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const existingCustomAttributes = (worker.customAttributes ?? []).filter(
          (a) => a != null && a != undefined,
        );

        const newPassportNumber =
          values.passportNumber ||
          existingCustomAttributes.find((a) => a?.ref === 'passportNumber')
            ?.value;

        const newPassportIssueCountry =
          values.passportIssueCountry ||
          existingCustomAttributes.find(
            (a) => a!.ref === 'passportIssueCountry',
          )?.value;

        const cleanedExistingCustomAttributes: CustomAttributeInput[] =
          existingCustomAttributes
            .filter(
              (a) =>
                a.ref != 'passportNumber' &&
                a.ref != 'passportIssueCountry' &&
                a.ref != 'residencyStatus' &&
                a.ref != 'residencyStatusWithMandatoryValidVisa',
            )
            .map((a) => ({
              ref: a.ref,
              value: a.value || '',
            }));

        const customAttributes: CustomAttributeInput[] = [
          ...cleanedExistingCustomAttributes,
          {
            ref: 'passportNumber',
            value: newPassportNumber || '',
          },
          {
            ref: 'passportIssueCountry',
            value: newPassportIssueCountry || '',
          },
          {
            ref: 'residencyStatusWithMandatoryValidVisa',
            value: values.residencyStatus || '',
          },
        ];

        const userInput: UserInput = {
          customAttributes,
          firstName: values.firstName || '',
          lastName: values.lastName || '',
        };
        await userActions.updateUser(worker.id, userInput);

        const newDob = formatDobForApi(values.dob);
        const currentDob = worker.dob || '';
        if (newDob && newDob !== currentDob) {
          await getApolloClient().mutate({
            mutation: PROCESS_PARTIAL_UPDATES,
            variables: {
              updates: [
                {
                  objectId: worker.id,
                  objectType: ObjectType.User,
                  attributeName: 'dob',
                  value: newDob,
                },
              ],
              allOrNothingUpdate: true,
            },
          });
        }

        await fetchDetailedWorkerById(worker.id);
        onClose();
      } catch (error) {
        console.error('Error updating work eligibility:', error);
        formik.setStatus(
          error instanceof Error ? error.message : 'Update failed',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.setValues(initialValues);
      setCountryFilter('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValues]);

  const filteredCountryOptions = useMemo(() => {
    if (!countryFilter.trim()) return PASSPORT_COUNTRY_OPTIONS;

    const searchTerm = countryFilter.trim().toLowerCase();

    return PASSPORT_COUNTRY_OPTIONS.filter(
      (o) =>
        o.label.toLowerCase().includes(searchTerm) ||
        o.value.toLowerCase().includes(searchTerm),
    );
  }, [countryFilter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">
            Work Eligibility
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {formik.status && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {formik.status}
          </div>
        )}

        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="work-eligibility-residency"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Residency status
            </label>
            <ActionListboxField
              id="work-eligibility-residency"
              value={formik.values.residencyStatus}
              onChange={(value) =>
                formik.setFieldValue('residencyStatus', value)
              }
              options={[...RESIDENCY_STATUS_OPTIONS]}
              placeholder="Select residency status"
              noneOptionLabel="Not specified"
            />
          </div>

          <div>
            <label
              htmlFor="dob"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Date of birth
            </label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={formik.values.dob}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="passportNumber"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Passport number
            </label>
            <input
              type="text"
              id="passportNumber"
              name="passportNumber"
              value={formik.values.passportNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="passportIssueCountry"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Passport issue country
            </label>
            <Listbox
              value={formik.values.passportIssueCountry}
              onChange={(value) =>
                formik.setFieldValue('passportIssueCountry', value)
              }
            >
              <div className="relative">
                <Listbox.Button className={actionListboxButtonClassName}>
                  <span className="block truncate">
                    {formik.values.passportIssueCountry
                      ? (PASSPORT_COUNTRY_OPTIONS.find(
                          (o) => o.value === formik.values.passportIssueCountry,
                        )?.label ?? formik.values.passportIssueCountry)
                      : 'Select country'}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={React.Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    <li className="sticky top-0 z-10 bg-white px-2 py-1.5 ring-1 ring-black/5">
                      <input
                        type="text"
                        placeholder="Search countries..."
                        value={countryFilter}
                        onChange={(e) => setCountryFilter(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className={`w-full px-2 py-1.5 text-sm ${actionFieldInputClassName}`}
                      />
                    </li>
                    {filteredCountryOptions.length === 0 ? (
                      <li className="px-4 py-2 text-gray-500">
                        No countries match
                      </li>
                    ) : (
                      filteredCountryOptions.map((opt) => (
                        <Listbox.Option
                          key={opt.value}
                          className={({ active }) =>
                            `relative cursor-default py-2 pr-4 pl-10 select-none ${
                              active
                                ? actionListboxOptionActiveClassName
                                : 'text-gray-900'
                            }`
                          }
                          value={opt.label}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? 'font-medium' : 'font-normal'
                                }`}
                              >
                                {opt.label}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    )}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          <div>
            <label
              htmlFor="work-eligibility-last-name"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Last name as on passport
            </label>
            <input
              type="text"
              id="work-eligibility-last-name"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="work-eligibility-first-name"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              First name as on passport
            </label>
            <input
              type="text"
              id="work-eligibility-first-name"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${actionPrimaryButtonClassName}`}
            >
              {isSubmitting ? 'Updating…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkEligibilityModal;
